import { useEffect, useRef, useState } from 'react';
import './SecurityCeremony.css';
import { lockScroll, unlockScroll } from '../scrollLock';

/**
 * THE WAIT BETWEEN "SEND CODE" AND THE CODE ARRIVING.
 *
 * An SMS takes about 1.5s and a WhatsApp message about 7s. Left with a plain
 * disabled button, people press Enter again, or decide the site is broken.
 * This covers the form for the duration and narrates what the server is doing.
 *
 * THE HONESTY RULE that shapes this file: never show a step as finished before
 * it has finished. There is no percentage bar and no fake completion. The step
 * labels advance on measured medians, but the last step HOLDS, however long the
 * request takes — a slow WhatsApp send parks on "Sending your one-time code"
 * instead of claiming success. Only the real API response ends the ceremony.
 *
 * The step order is not decoration either; it is the order the server actually
 * works in (TLS, then credentials, then dispatch).
 */

const FLOWS = {
  /** Asking the server to send a code. */
  dispatch: [
    { label: 'Securing the connection', holdMs: 700 },
    { label: 'Verifying your credentials', holdMs: 1200 },
    { label: 'Sending your one-time code', holdMs: null },
  ],
  /**
   * A request that MAY turn into an OTP challenge, depending on whether an
   * admin has that gate switched on. The last step stays neutral because at
   * this point the browser genuinely does not yet know whether a code is being
   * sent — claiming "sending your code" here would sometimes be false.
   */
  submit: [
    { label: 'Securing the connection', holdMs: 700 },
    { label: 'Verifying your credentials', holdMs: 1200 },
    { label: 'Processing your request securely', holdMs: null },
  ],
  /** Submitting the code the user typed. */
  verify: [
    { label: 'Encrypting your code', holdMs: 600 },
    { label: 'Checking it against the server', holdMs: 1100 },
    { label: 'Completing sign-in', holdMs: null },
  ],
  /** Submitting a code that authorises money movement. */
  authorise: [
    { label: 'Encrypting your code', holdMs: 600 },
    { label: 'Verifying your authorisation', holdMs: 1100 },
    { label: 'Completing the request', holdMs: null },
  ],
};

export default function SecurityCeremony({
  active,
  flow = 'dispatch',
  title = 'Securing your request',
  note,
}) {
  const steps = FLOWS[flow] || FLOWS.dispatch;
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setCurrent(0);
      clearTimeout(timerRef.current);
      return undefined;
    }

    let step = 0;
    setCurrent(0);

    const advance = () => {
      const { holdMs } = steps[step];
      // A null hold is the final step: stop scheduling and wait for the
      // response, however long it takes.
      if (holdMs == null) return;
      timerRef.current = setTimeout(() => {
        step += 1;
        setCurrent(step);
        advance();
      }, holdMs);
    };
    advance();

    return () => clearTimeout(timerRef.current);
  }, [active, flow, steps]);

  // Stop the page behind the overlay scrolling under the user's fingers.
  // Shared lock (see scrollLock.js): restoring a saved value here used to
  // leave the page stuck at overflow:hidden when the OTP dialog closed in
  // between.
  useEffect(() => {
    if (!active) return undefined;
    const key = {};
    lockScroll(key);
    return () => unlockScroll(key);
  }, [active]);

  if (!active) return null;

  return (
    <div
      className="sec-ceremony"
      role="alertdialog"
      aria-modal="true"
      aria-live="polite"
      aria-label={title}
      // Covers the viewport, so the form underneath cannot be clicked while a
      // request is in flight. The keyboard is a separate matter and is handled
      // where it has to be — each submit handler returns early when it is
      // already submitting, which is what stops a second Enter press.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sec-ceremony__card">
        <div className="sec-ceremony__shield" aria-hidden="true">
          <span className="sec-ceremony__ring" />
          <svg viewBox="0 0 24 24" width="34" height="34">
            <path
              fill="currentColor"
              d="M12 1.6 4 4.9v6.2c0 4.9 3.4 9.5 8 10.6 4.6-1.1 8-5.7 8-10.6V4.9l-8-3.3Zm-1.2 14.2-3.3-3.3 1.4-1.4 1.9 1.9 4.7-4.7 1.4 1.4-6.1 6.1Z"
            />
          </svg>
        </div>

        <h4 className="sec-ceremony__title">{title}</h4>

        <ul className="sec-ceremony__steps">
          {steps.map((step, index) => {
            const state = index < current ? 'done' : index === current ? 'active' : 'todo';
            return (
              <li key={step.label} className={`sec-ceremony__step is-${state}`}>
                <span className="sec-ceremony__bullet" aria-hidden="true">
                  {state === 'done' ? '✓' : ''}
                </span>
                <span>{step.label}</span>
              </li>
            );
          })}
        </ul>

        <p className="sec-ceremony__note">
          {note || 'Encrypted end to end. Please keep this page open.'}
        </p>
      </div>
    </div>
  );
}
